import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Circle, ChevronDown, ChevronRight } from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { listarUsuarios } from '../../usuarios/api';
import { useHistoricoDaBase } from '../hooks';
import type { BaseComercialHistorico as BaseComercialHistoricoEntry } from '../api';

const ACTION_LABEL: Record<string, string> = {
  created: 'Criada',
  updated: 'Atualizada',
  service_added: 'Serviço adicionado',
  service_removed: 'Serviço removido',
  item_added: 'Item adicionado',
  item_updated: 'Item atualizado',
  item_removed: 'Item removido',
  version_created: 'Nova versão gerada',
  approval_requested: 'Aprovação solicitada',
  approval_approved: 'Aprovada',
  approval_rejected: 'Rejeitada',
  published: 'Publicada',
  suspended: 'Suspensa',
  reactivated: 'Reativada',
  cancelled: 'Cancelada',
  superseded: 'Substituída',
  expired: 'Expirada',
  adjustment_applied: 'Reajuste aplicado',
  adjustment_pending: 'Reajuste pendente',
  discount_approved: 'Desconto aprovado',
  conditions_reviewed: 'Condições revisadas',
  attachment_added: 'Anexo adicionado',
  attachment_removed: 'Anexo removido',
  expired_use_authorized: 'Uso expirado autorizado',
  admin_override: 'Override administrativo',
};

const ACTION_COLOR: Record<string, string> = {
  approval_approved: 'text-green-700',
  approval_rejected: 'text-red-700',
  cancelled: 'text-gray-500',
  suspended: 'text-amber-700',
  superseded: 'text-slate-600',
  expired: 'text-red-700',
  published: 'text-emerald-700',
  admin_override: 'text-red-700',
};

/**
 * Trilha de auditoria da Base Comercial (task 11.4, RN-COM-025).
 *
 * Consome `commercial_base_change_history` via `GET
 * /commercial-bases/:id/history`. Uma linha por campo alterado — exibe
 * usuário, data/hora, campo, valor anterior, novo valor e versão,
 * no padrão da timeline de cliente/oportunidade.
 */
export const BaseComercialHistorico: React.FC<{ baseId: string }> = ({ baseId }) => {
  const { data, isLoading, isError, error } = useHistoricoDaBase(baseId, { limit: 100 });

  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    (usuariosResp?.data ?? []).forEach((u) => m.set(u.id, u.name));
    return m;
  }, [usuariosResp]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <Alert variant="error">
        Erro ao carregar histórico: {(error as Error)?.message ?? 'Falha desconhecida.'}
      </Alert>
    );
  }
  if (!data || data.data.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Nenhum evento registrado nesta Base ainda.
      </p>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-2 top-2 bottom-2 w-px bg-gray-200" aria-hidden />
      <ul className="space-y-3">
        {data.data.map((h) => (
          <TimelineEntry key={h.id} entry={h} userNameById={userNameById} />
        ))}
      </ul>
      {data.total > data.data.length && (
        <p className="mt-3 text-center text-xs text-gray-500">
          Mostrando {data.data.length} de {data.total} eventos.
        </p>
      )}
    </div>
  );
};

const TimelineEntry: React.FC<{
  entry: BaseComercialHistoricoEntry;
  userNameById: Map<string, string>;
}> = ({ entry, userNameById }) => {
  const [showDiff, setShowDiff] = useState(false);
  const label = ACTION_LABEL[entry.action] ?? entry.action;
  const colorClass = ACTION_COLOR[entry.action] ?? 'text-gray-800';

  return (
    <li className="relative pl-8">
      <span className="absolute left-1 top-2" aria-hidden>
        <Circle className={`h-3 w-3 fill-current ${colorClass}`} />
      </span>
      <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={`text-sm font-semibold ${colorClass}`}>{label}</span>
          <span className="text-xs text-gray-500">
            v{entry.versao} • {formatDateTime(entry.changedAt)}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-gray-500">
          {entry.changedBy ? (
            <>
              por{' '}
              <span className="font-medium text-gray-600">
                {userNameById.get(entry.changedBy) ?? 'usuário desconhecido'}
              </span>
            </>
          ) : (
            <>por sistema</>
          )}
        </div>
        {entry.campo && (
          <div className="mt-2 flex flex-wrap gap-1">
            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-700">
              {fieldLabel(entry.campo)}
            </span>
          </div>
        )}
        {entry.observacao && (
          <blockquote className="mt-2 border-l-2 border-gray-200 pl-3 text-xs italic text-gray-600">
            {entry.observacao}
          </blockquote>
        )}
        {(entry.valorAnterior || entry.valorNovo) && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setShowDiff((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              {showDiff ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {showDiff ? 'Ocultar detalhes' : 'Ver detalhes'}
            </button>
            {showDiff && (
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Antes
                  </div>
                  <pre className="overflow-x-auto rounded bg-red-50 p-2 text-[10px] text-red-900">
                    {entry.valorAnterior ?? '(vazio)'}
                  </pre>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Depois
                  </div>
                  <pre className="overflow-x-auto rounded bg-green-50 p-2 text-[10px] text-green-900">
                    {entry.valorNovo ?? '(vazio)'}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

function fieldLabel(campo: string): string {
  const map: Record<string, string> = {
    nome: 'Nome',
    modalidade: 'Modalidade',
    escopo: 'Escopo',
    data_inicio: 'Data de início',
    data_fim: 'Data final',
    moeda: 'Moeda',
    responsavel_comercial_id: 'Responsável comercial',
    desconto_maximo_percentual: 'Desconto máximo',
    reajuste_periodicidade: 'Periodicidade do reajuste',
    reajuste_index_slug: 'Índice do reajuste',
    reajuste_percentual_fixo: 'Percentual fixo do reajuste',
    situacao: 'Situação',
  };
  return map[campo] ?? campo;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR');
}
